// podcastHelpers.js (or wherever ROUTE_SCENES lives)

import { COMM_TOWER_POLYGON, KELLER_FIRE_POLYGON } from "./firePolyHelpers";

//LOCATIONS AND POLYGONS 
const DEFAULT_LOCATION_1 = { lat: 37.722941, lng: -122.132001 };
const DEFAULT_LOCATION_2 = { lat: 37.726041, lng: -122.132001 };

const DEFAULT_ZOOM = 16;

export const SCENE_SYMBOLS = {
  DEFAULT_LOCATION: DEFAULT_LOCATION_1,
  DEFAULT_LOCATION_1,
  DEFAULT_LOCATION_2,
  DEFAULT_ZOOM,
  COMM_TOWER_POLYGON,
  // add more here as you start using them:
  // KELLER_FIRE_POLYGON, etc.
};

export const normalizePolygonPath = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((pt) => {
      if (!pt) return null;
      if (Array.isArray(pt) && pt.length >= 2) {
        const lat = Number(pt[0]);
        const lng = Number(pt[1]);
        if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
        return null;
      }
      const lat = Number(pt.lat ?? pt.latitude);
      const lng = Number(pt.lng ?? pt.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
      return null;
    })
    .filter(Boolean);
};

export const parseDurationToSeconds = (s) => {
  const str = String(s || "").toLowerCase();
  const hr  = str.match(/([\d.]+)\s*h(?:r|rs)?/i);
  const min = str.match(/([\d.]+)\s*m(?:in|ins)?/i);
  const sec = str.match(/([\d.]+)\s*s(?:ec|ecs)?/i);

  const hours   = hr  ? parseFloat(hr[1])  : 0;
  const minutes = min ? parseFloat(min[1]) : 0;
  const seconds = sec ? parseFloat(sec[1]) : 0;

  return Math.round(hours * 3600 + minutes * 60 + seconds);
};

// ✅ NOW takes a symbols map (defaults to SCENE_SYMBOLS)
export const resolveSceneSymbol = (val, symbols = SCENE_SYMBOLS) => {
  if (typeof val === "string" && Object.prototype.hasOwnProperty.call(symbols, val)) {
    return symbols[val];
  }
  return val;
};

// ✅ normalize one scene object with symbols
export const normalizeScene = (s, symbols = SCENE_SYMBOLS) => {
  const out = { ...s };

  if (s.camera) {
    out.camera = {
      ...s.camera,
      center: resolveSceneSymbol(s.camera.center, symbols),
      zoom: resolveSceneSymbol(s.camera.zoom, symbols),
    };
  }

  if (Array.isArray(s.firePolygons)) {
    out.firePolygons = s.firePolygons.map((fp) => ({
      ...fp,
      path: resolveSceneSymbol(fp.path, symbols),
    }));
  }

  // (optional but common) if you store symbols in icons later:
  if (Array.isArray(s.icons)) {
    out.icons = s.icons.map((ic) => ({
      ...ic,
      lat: resolveSceneSymbol(ic.lat, symbols),
      lng: resolveSceneSymbol(ic.lng, symbols),
      position: resolveSceneSymbol(ic.position, symbols),
    }));
  }

  return out;
};

// ✅ normalize array with override
export const normalizeScenes = (scenes, { symbols } = {}) => {
  const sym = symbols || SCENE_SYMBOLS;
  return Array.isArray(scenes) ? scenes.map((s) => normalizeScene(s, sym)) : [];
};

/// ROUTES CHECKLIST (Showing the route bullets in the UI that can be clicked)
export const routeChecklistItems = [
  { key: "Intro", title: "Intro" },
  { key: "2151", nodeId: "2151", title: "Estudillo Ave" },
  { key: "1047", nodeId: "1047",  title: "Grand Avenue" },
  { key: "28", nodeId: "28",  title: "I-580 North" },
  { key: "Conclusion", title: "Conclusion" },
];

/// ROUTE TIMINGS (Showing routes in podcast based on time)

export const routesAudioTimings = {
  "Intro": 0,
  "Estudillo Ave": 59,   // ~0:59 (Route 1 intro starts: "Option One...")
  "Grand Avenue": 197,   // ~3:17 (Route 2 intro starts)
  "Conclusion": 307      // ~5:07 (start of planning-only wrap-up)
};

export const routesAudioTimings_ES = {
  "Intro": 0,
  "Estudillo Ave": 56,    // 路线 1 开始介绍（往西北 Estudillo）
  "Grand Avenue": 196,    // 路线 2 开始介绍
  "Conclusion": 323       // 官方指令与收尾段开始
};

export const routesAudioTimings_old_ES = {
  "Intro": 0,
  "Estudillo Ave": 78,                  // 2 : 45  → 2*60+45
  "Grand Avenue": 165,        // 1 : 00
  "Conclusion": 267,                 // 5 : 15
};

export const routesAudioTimings_old2 = {
  "Intro": 0,
  "Estudillo Ave": 50,                  // 2 : 45  → 2*60+45
  "Grand Avenue": 98,        // 1 : 00
  "150th Avenue": 156,            // 3 : 49
  "Conclusion": 200,                 // 5 : 15
};




  // ── Resource snippet timings (seconds) ───────────────────────────────
export const RESOURCE_SEGMENTS_BY_LANG = {
    EN: [
      { key: "alerts", start: 0, end: 78 }, 
      { key: "nixle",  start: 83,  end: 165 }, 
      { key: "zones",  start: 167,  end: 245 }, 
      { key: 'watch',  start: 245,  end: 301 }
    ],
    ES: [
      // If the Spanish cut uses the same timings, duplicate; otherwise adjust.
      { key: "alerts", start: 59, end: 59 }, //end: 162 },
      { key: "nixle",  start: 181,  end: 220 },
      { key: "zones",  start: 221,  end: 284 },
      { key: 'watch',  start: 290,  end: 342 }
    ],
    MN: [
      {key: "alerts", start: 43, end: 43 }, //end: 97 },
      { key: "nixle", start: 97, end: 161.5 },
      { key: "zones", start: 162, end: 230 },
      { key: 'watch', start: 230.5, end: 285 }
    ]
  };
  
  export const getResourceSegments = (lang = "EN") =>
    RESOURCE_SEGMENTS_BY_LANG[lang] || RESOURCE_SEGMENTS_BY_LANG.EN;


  export const RESOURCE_META_BY_LANG = {
    EN: {
        "alerts": {
          "title": "AC Alerts",
          "subtitle": "Get instant evacuation and emergency notifications to your phone.",
          "href": "https://member.everbridge.net/453003085612570/new",
          "howTo": [
            "Go to AC Alert signup page.",
            "Create account with name, username, password.",
            "Set primary location and address; verify.",
            "Add contacts; choose subscriptions; finish."
          ]
        },
        "nixle": {
          "title": "Nixle Alerts",
          "subtitle": "Get instant evacuation and emergency notifications to your phone.",
          "href": "https://local.nixle.com/register/",
          "howTo": [
            "Go to Nixle registration page.",
            "Enter email, password, name, phone; signup.",
            "Add locations; enter complete addresses.",
            "Enable Subscribe to official government agencies."
          ]
        },
        "zones": {
          "title": "Know your zone",
          "subtitle": "Find your evacuation zone and plan your route before you need it.",
          "href": "https://protect.genasys.com/search",
          "howTo": [
            "Go to Know Your Zone search.",
            "Enter home or work address.",
            "View map callout showing Zone ID.",
            "Write down and save Zone ID."
          ]
        },
        "watch": {
          "title": "Install Watch Duty",
          "subtitle": "See live wildfire incident maps and local updates for your area.",
          "href": "https://app.watchduty.org/",
          "howTo": [
            "Find Watch Duty in your app store.",
            "Install; allow Location Services and Notifications.",
            "Select your state and county.",
            "Receive real-time wildfire notifications."
          ]
        }
    },
  
    ES: {
      alerts: {
        title: "AC Alerts",
        subtitle: "Recibe notificaciones instantáneas de evacuación y emergencias en tu teléfono.",
        href: "https://member.everbridge.net/453003085612570/new",
        howTo: [
          "Ve a la página de registro de AC Alerts.",
          "Crea una cuenta con tu nombre, usuario y contraseña.",
          "Configura tu ubicación principal y dirección; verifica.",
          "Añade contactos, elige suscripciones y finaliza."
        ]
      },
      nixle: {
        title: "Alertas Nixle",
        subtitle: "Recibe notificaciones instantáneas de evacuación y emergencias en tu teléfono.",
        href: "https://local.nixle.com/register/",
        howTo: [
          "Ve a la página de registro de Nixle.",
          "Ingresa correo, contraseña, nombre y teléfono; regístrate.",
          "Añade ubicaciones e ingresa las direcciones completas.",
          "Activa la suscripción a agencias oficiales del gobierno."
        ]
      },
      zones: {
        title: "Conoce tu zona",
        subtitle: "Encuentra tu zona de evacuación y planifica tu ruta antes de que la necesites.",
        href: "https://protect.genasys.com/search",
        howTo: [
          "Ve a la búsqueda de Know Your Zone.",
          "Ingresa la dirección de tu casa o trabajo.",
          "Mira el mapa y el recuadro con tu ID de zona.",
          "Anota y guarda tu ID de zona."
        ]
      },
      watch: {
        title: "Instala Watch Duty",
        subtitle: "Consulta mapas de incendios forestales en vivo y actualizaciones locales de tu zona.",
        href: "https://app.watchduty.org/",
        howTo: [
          "Busca Watch Duty en tu tienda de aplicaciones.",
          "Instálala y permite Ubicación y Notificaciones.",
          "Selecciona tu estado y condado.",
          "Recibe alertas de incendios en tiempo real."
        ]
      }
    },
  
    MN: {
      alerts: {
        title: "訂閱 AC Alerts",
        subtitle: "即時在手機上收到疏散與緊急通知。",
        href: "https://member.everbridge.net/453003085612570/new",
        howTo: [
          "前往 AC Alerts 註冊頁面。",
          "用姓名、使用者名稱與密碼建立帳號。",
          "設定主要地點與地址並完成驗證。",
          "新增聯絡人並選擇要訂閱的警報，完成設定。"
        ]
      },
      nixle: {
        title: "訂閱 Nixle 警報",
        subtitle: "即時在手機上收到疏散與緊急通知。",
        href: "https://local.nixle.com/register/",
        howTo: [
          "前往 Nixle 註冊頁面。",
          "輸入電子郵件、密碼、姓名與電話後註冊。",
          "新增地點並填寫完整地址。",
          "開啟訂閱官方政府機關的通知。"
        ]
      },
      zones: {
        title: "查詢你的疏散分區",
        subtitle: "提前確認你的疏散分區並規劃撤離路線。",
        href: "https://protect.genasys.com/search",
        howTo: [
          "前往 Know Your Zone 查詢頁面。",
          "輸入住家或工作地址。",
          "查看地圖標註的分區 ID。",
          "記下並保存你的分區 ID。"
        ]
      },
      watch: {
        title: "安裝 Watch Duty",
        subtitle: "即時查看野火事件地圖與你所在地區的更新。",
        href: "https://app.watchduty.org/",
        howTo: [
          "在手機應用商店搜尋 Watch Duty。",
          "安裝後允許定位服務與通知。",
          "選擇你的州與郡/縣。",
          "接收即時野火警報。"
        ]
      }
    }
  };
  
  export const getResourceMetaMap = (lang = "EN") =>
    RESOURCE_META_BY_LANG[lang] || RESOURCE_META_BY_LANG.EN;

  export const RESOURCE_UI_I18N = {
    EN: {
      title: "Next steps",
      share: "Share with neighbors",
      share_desc: "Share this content with neighbours and build your buddy system.",
    },
    ES: {
      title: "Siguientes pasos",
      share: "Comparte con vecinos",
      share_desc: "Comparte este contenido con tus vecinos y organicen una red de apoyo.",
    },
    // Mandarin (Traditional Chinese) — code MN
    MN: {
      title: "後續步驟",
      share: "與鄰居分享",
      share_desc: "把這些內容分享給鄰居，一起建立互助夥伴。",
    },
  };
  
  export const getResourceUiI18n = (lang = "EN") =>
    RESOURCE_UI_I18N[lang] || RESOURCE_UI_I18N.EN;


// ── FAQ items (language-aware) ────────────────────────────────────────
export const FAQ_ITEMS_BY_LANG = {
  EN: [
    {
      key: "whenLeave",
      q: "When should I leave?",
      a: `Go early—don’t wait for an official order. If you see flames, smell smoke, or notice neighbors leaving, it’s time to go.
  Many wildfires ignite or spread so fast that official alerts may not reach you in time, especially during Red Flag Days when strong winds and dry air make fires harder to contain.
  
  Stay tuned to multiple sources: AC Alert, Watch Duty, local radio, trusted social media, and your neighbors.
  Leaving early reduces congestion and gives firefighters more room to operate safely.
  
  On high-risk days, plan ahead—pack your car, face it toward your exit route, or consider spending the day somewhere safer off the hills.`
    },
    {
      key: "whereGo",
      q: "Where do I go and how do I get out safely?",
      a: `Prepare now:
  • Identify at least two evacuation routes and make sure they lead you away from areas with trees, brush, or heavy vegetation.
  • Choose a family meeting point or Temporary Refuge Area (TRA) such as a paved lot, wide intersection, or open field with minimal vegetation.
  • Practice your routes using WUI-Go!.
  • Keep a paper map in your go-bag in case cell service or GPS fails.
  
  If traffic stops or visibility drops, it is usually safest to stay inside your vehicle on a paved road. Your car provides partial protection from heat, embers, smoke, and flying debris.
  Keep windows and vents closed, set air to recirculate/internal only, and turn on headlights for visibility.
  
  Leave your car only as an absolute last resort, and only if emergency officials instruct you to. If you must exit, move toward a wide, paved area with little vegetation and stay low to avoid heat and smoke.`
    },
    {
      key: "notHome",
      q: "What if I’m not home when a wildfire breaks out?",
      a: `Don’t drive back in—roads can close or become dangerous within minutes. Activate your Buddy Household so trusted neighbors can check on pets or loved ones with mobility or medical needs. 
  Stay in contact by phone, confirm if your buddy is evacuating, and monitor from a safe place using Watch Duty or AC Alert.
  
  Before an emergency:
  • Exchange keys and contact info with trusted neighbors.
  • Share your emergency plan, medical details, and go-bag locations.
  • Include children and caregivers in simple “what-if” talks so everyone knows emergency contacts and meeting points.
  • Designate a pet buddy who can retrieve animals if you’re away and keep carriers, leashes, and medications labeled.
  • If you or a loved one use powered medical devices, register for PG&E’s Medical Baseline program and plan for backup power or manual mobility aids.`
    },
    {
      key: "routeDefinition",
      q: "How are my evacuation routes defined in this map?",
      a: `The routes shown represent your most direct path downhill toward I-580 and an alternate route optimized for safety, based on simulated fire exposure and total community clearance time.
  These modeled routes prioritize survivability and smooth traffic flow, not everyday convenience.
  
  In a real emergency, always follow directions from first responders, as fire and road conditions can change quickly.`
    },
    {
      key: "lakeChabot",
      q: "What about Lake Chabot Road in the simulations?",
      a: `Lake Chabot Road is not considered a viable route in this simulation because the segment between Astor Drive and Chabot Terrace remains closed to vehicle traffic by the City of San Leandro.
  For studies that include potential repair scenarios and reopening conditions, please refer to WUI-Go!’s forthcoming technical report.`
    },
    {
      key: "leaveOnTime",
      q: "What does “leaving on time” mean in the model?",
      a: `In the model, “leaving on time” means your car is pulling out of the driveway about 15 minutes after you first realize there’s danger and decide to evacuate.
  The model treats a “10-minute delay” as taking roughly 25 minutes before you’re on the road.
  
  That 15-minute window reflects what households typically need to recognize the threat, communicate (“We’re leaving now”), secure pets and medications, close up the house, gather essentials, and get everyone into the car.
  Even highly prepared households usually need 5–15 minutes in real conditions—yet most people underestimate how long this actually takes.
  
  Delays in evacuee departure are one of the biggest contributors to wildfire fatalities, right alongside extreme fire behavior and alerting failures.
  In no-notice wildfires, those delays can be life-threatening: residents may only have a few minutes between realizing what’s happening and needing to be out the door.`
    },
    {
      key: "wellPrepared",
      q: "What does it mean to be “well prepared” for evacuation?",
      a: `Being well-prepared means you understand local alerts and hazards, have multiple ways to get information if systems fail, and use a simple buddy check so no one misses a cue—including buddies for pets or kids if someone isn’t home.
  
  You’ve already decided who grabs what and you have a plan for “what if I’m at work, the kids are alone, or the pet-sitter is there.”
  You know your primary and backup routes without relying on GPS, keep your go-bag by the door, and practice a quick grab for everyday essentials like medications.
  You park your car nose-out so whenever the cue comes—whether you’re home or not—you or your designated buddy can leave calmly and immediately.`
    },
    {
      key: "timeReliability",
      q: "How reliable are the evacuation times?",
      a: `Evacuation times are estimates based on realistic traffic, fire-spread, and behavior patterns—not guarantees or predictions.
  
  In our simulations, full neighborhood clearance typically takes about one hour but can vary by roughly ±15–30 minutes depending on traffic, when people start leaving, and whether major facilities like hospitals are also evacuating.
  
  These results highlight the pattern, not a promise: the earlier you leave, the safer and smoother your evacuation will be.`
    },
    {
      key: "modelBuilt",
      q: "How was this evacuation model built?",
      a: `WUI-Go!’s simulations were developed using a UC Berkeley Digital Twin platform designed to model high-resolution traffic flow and fire-spread interactions.
  
  Key assumptions are based on responses from the Bay-O-Vista Resident Survey (Sep 2025):
  • An average of two vehicles per household.
  • A 15-minute preparation time before departure.
  • Departure times for other residents based on a best-case, self-reported readiness (15 to 75 minutes).
  
  Kindred Hospital is modeled as sheltering in place, consistent with the hospital’s official emergency procedures—representing a best-case condition where few additional vehicles or ambulances enter Benedict Drive during evacuation.
  The Fairmont Ridge fire model reflects a realistically dangerous fire.
  
  The goal of this model is to help residents and emergency responders understand evacuation timing, test different strategies, and prepare for faster, safer evacuations before a real emergency occurs.
  
  (WUI-Go!’s evacuation technology is used under an exclusive evaluation agreement with the University of California).`
    }
  ],
    ES: [
      {
        key: "preplanning",
        q: "Is this information for pre-planning only?",
        a: "Yes. It’s designed to help you prepare before an evacuation. During a real event, always follow official instructions and alerts."
      },
      {
        key: "assumptions",
        q: "What are the assumptions behind the model?",
        a: "The digital twin simulated all houses evacuating at the same time. Based on the model results, we estimate the evacuation time for each resident along their familiar routes."
      },
      {
        key: "hospital",
        q: "Was the hospital considered in the evacuation?",
        a: "Yes."
      }
    ]
  };
  
export const getFAQItems = (lang = "EN") =>
    FAQ_ITEMS_BY_LANG[lang] || FAQ_ITEMS_BY_LANG.EN;


const pointInPolygonPixels = (x, y, poly) => {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x;
    const yi = poly[i].y;
    const xj = poly[j].x;
    const yj = poly[j].y;
    const crossesY = (yi > y) !== (yj > y);
    const intersects = crossesY && x < (((xj - xi) * (y - yi)) / (yj - yi)) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
};

export const getSmokeOverlayKey = (fp) => {
  const pathSignature = (fp?.path || [])
    .map((pt) => {
      const lat = Number(pt?.lat);
      const lng = Number(pt?.lng);
      return `${Number.isFinite(lat) ? lat.toFixed(6) : ""},${Number.isFinite(lng) ? lng.toFixed(6) : ""}`;
    })
    .join("|");

  return JSON.stringify({
    polygonName: fp?.polygon_name || "",
    path: pathSignature,
    wind: fp?.wind || null,
    density: fp?.density || null
  });
};

export const createSmokeEmberOverlay = ({ google, map, path, wind = {}, density = 1 }) => {
  if (!google?.maps?.OverlayView || !map || !Array.isArray(path) || path.length < 3) return null;

  const overlay = new google.maps.OverlayView();
  const particles = [];
  const smokeWindX = Number.isFinite(Number(wind.x)) ? Number(wind.x) : -1;
  const smokeWindY = Number.isFinite(Number(wind.y)) ? Number(wind.y) : 0.55;
  const particleDensity = Math.max(0.2, Math.min(3, Number(density) || 1));
  const padding = 120;

  let div = null;
  let canvas = null;
  let ctx = null;
  let raf = 0;
  let width = 0;
  let height = 0;
  let localPolygon = [];
  let localBounds = null;

  const randomPointInPolygon = () => {
    if (!localPolygon.length || !localBounds) return null;
    const minX = localBounds.minX;
    const maxX = localBounds.maxX;
    const minY = localBounds.minY;
    const maxY = localBounds.maxY;

    for (let i = 0; i < 60; i++) {
      const x = minX + Math.random() * (maxX - minX || 1);
      const y = minY + Math.random() * (maxY - minY || 1);
      if (pointInPolygonPixels(x, y, localPolygon)) return { x, y };
    }

    return {
      x: localPolygon.reduce((sum, pt) => sum + pt.x, 0) / localPolygon.length,
      y: localPolygon.reduce((sum, pt) => sum + pt.y, 0) / localPolygon.length
    };
  };

  const spawn = (kind) => {
    const pt = randomPointInPolygon();
    if (!pt) return;

    const isEmber = kind === "ember";
    particles.push({
      x: pt.x,
      y: pt.y,
      vx: smokeWindX * (isEmber ? 1.5 : 0.55) + (Math.random() - 0.5) * 0.4,
      vy: smokeWindY * (isEmber ? 1.5 : 0.55) + (Math.random() - 0.5) * 0.3 - (isEmber ? 0 : 0.15),
      life: 0,
      maxLife: isEmber ? 40 + Math.random() * 60 : 120 + Math.random() * 80,
      size: isEmber ? 2 + Math.random() * 3 : 8 + Math.random() * 10,
      kind
    });
  };

  const drawSourcePolygon = () => {
    if (!ctx || localPolygon.length < 3) return;
    ctx.beginPath();
    localPolygon.forEach((pt, idx) => {
      if (idx === 0) ctx.moveTo(pt.x, pt.y);
      else ctx.lineTo(pt.x, pt.y);
    });
    ctx.closePath();
    ctx.fillStyle = "rgba(80, 80, 80, 0.18)";
    ctx.fill();
    ctx.strokeStyle = "rgba(120, 120, 120, 0.35)";
    ctx.lineWidth = 2;
    ctx.stroke();
  };

  const tick = () => {
    if (!ctx || !canvas || !width || !height) return;

    ctx.clearRect(0, 0, width, height);
    drawSourcePolygon();

    const emberSpawns = Math.round(5 * particleDensity);
    for (let i = 0; i < emberSpawns; i++) spawn("ember");
    if (Math.random() < 0.5 * particleDensity) spawn("smoke");
    if (Math.random() < particleDensity) spawn("smoke");

    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.life += 1;
      if (p.life >= p.maxLife || p.x < -padding || p.x > width + padding || p.y < -padding || p.y > height + padding) {
        particles.splice(i, 1);
        continue;
      }

      p.vx += (Math.random() - 0.5) * 0.06;
      p.vy += (Math.random() - 0.5) * 0.06 - 0.008;
      p.x += p.vx;
      p.y += p.vy;

      const t = p.life / p.maxLife;
      const step = Math.floor(t * 4) / 4;

      if (p.kind === "ember") {
        const g = Math.floor(160 * (1 - step));
        ctx.fillStyle = `rgba(255, ${g}, 30, ${1 - step})`;
        const s = Math.max(1, Math.round(p.size * (1 - t * 0.3)));
        ctx.fillRect(Math.round(p.x), Math.round(p.y), s, s);
      } else {
        const v = 90 + Math.floor(step * 40);
        ctx.fillStyle = `rgba(${v}, ${v}, ${v}, ${(1 - step) * 0.55})`;
        const s = Math.max(2, Math.round(p.size * (1 + t * 0.8)));
        ctx.fillRect(Math.round(p.x), Math.round(p.y), s, s);
      }
    }

    raf = requestAnimationFrame(tick);
  };

  const resizeCanvas = () => {
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.round(width * dpr));
    canvas.height = Math.max(1, Math.round(height * dpr));
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = false;
  };

  overlay.onAdd = () => {
    div = document.createElement("div");
    div.style.position = "absolute";
    div.style.pointerEvents = "none";
    div.style.zIndex = "9999";

    canvas = document.createElement("canvas");
    canvas.style.position = "absolute";
    canvas.style.inset = "0";
    canvas.style.pointerEvents = "none";
    div.appendChild(canvas);

    ctx = canvas.getContext("2d");
    const panes = overlay.getPanes();
    panes.overlayLayer.appendChild(div);
    raf = requestAnimationFrame(tick);
  };

  overlay.draw = () => {
    if (!div || !canvas || !ctx) return;
    const projection = overlay.getProjection();
    if (!projection) return;

    const points = path
      .map((pt) => projection.fromLatLngToDivPixel(new google.maps.LatLng(pt.lat, pt.lng)))
      .filter(Boolean);
    if (points.length < 3) return;

    const minX = Math.min(...points.map((pt) => pt.x)) - padding;
    const minY = Math.min(...points.map((pt) => pt.y)) - padding;
    const maxX = Math.max(...points.map((pt) => pt.x)) + padding;
    const maxY = Math.max(...points.map((pt) => pt.y)) + padding;
    const nextWidth = Math.max(1, maxX - minX);
    const nextHeight = Math.max(1, maxY - minY);

    div.style.left = `${minX}px`;
    div.style.top = `${minY}px`;
    div.style.width = `${nextWidth}px`;
    div.style.height = `${nextHeight}px`;

    const sizeChanged = Math.round(nextWidth) !== Math.round(width) || Math.round(nextHeight) !== Math.round(height);
    width = nextWidth;
    height = nextHeight;
    localPolygon = points.map((pt) => ({ x: pt.x - minX, y: pt.y - minY }));
    localBounds = {
      minX: Math.min(...localPolygon.map((pt) => pt.x)),
      maxX: Math.max(...localPolygon.map((pt) => pt.x)),
      minY: Math.min(...localPolygon.map((pt) => pt.y)),
      maxY: Math.max(...localPolygon.map((pt) => pt.y))
    };

    if (sizeChanged || canvas.width === 0 || canvas.height === 0) resizeCanvas();
  };

  overlay.onRemove = () => {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    particles.length = 0;
    if (div?.parentNode) div.parentNode.removeChild(div);
    div = null;
    canvas = null;
    ctx = null;
  };

  overlay.setMap(map);
  return overlay;
};


  export const normalizeStreet = (s) => (s || '')
  .toLowerCase()
  .replace(/\./g, '')
  .replace(/\b(dr|drive|rd|road|st|street|ave|avenue|ct|court|ln|lane)\b/g, '')
  .replace(/\s+/g, ' ')
  .trim();

export const streetSimilarity = (a, b) => {
  a = normalizeStreet(a); b = normalizeStreet(b);
  if (!a || !b) return 0;
  if (a === b) return 1;
  if (a.includes(b) || b.includes(a)) return 0.9;
  const A = new Set(a.split(' ')), B = new Set(b.split(' '));
  const inter = [...A].filter(x => B.has(x)).length;
  const union = new Set([...A, ...B]).size;
  return inter / Math.max(1, union);
};

// Pull a street-ish string from user input like "2112 Lakeview drive"
export const extractStreetFromInput = (s) => {
  if (!s) return '';
  const m = s.match(/\d+\s+(.+)/);
  return normalizeStreet(m ? m[1] : s);
};

// Best guess at a link's street name (adapt to your schema if needed)
export const linkStreetName = (row) =>
  row?.name || row?.street || row?.st_name || row?.road || '';
